import React from 'react'
import { Element } from 'react-bulma-components'
import { formatDuration } from '../../utils/date'

/**
 * Interface pour les props du composant CountersTooltip
 */
interface CountersTooltipProps {
  /**
   * Données des compteurs à afficher
   * Keys are worker counter template _id (as string)
   */
  counters: Record<
    string,
    {
      allocation: { toCounter: number; toPeriod: number }
      workerCounter?: { template?: { name?: string } }
    }
  >
  /**
   * Map template _id → display name when API does not provide workerCounter.template.name
   * (e.g. week summary returns counters keyed by template id without populated template)
   */
  templateNamesMap?: Record<string, string>
}

/**
 * Composant pour les en-têtes de colonnes
 */
const ColumnHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Element
    renderAs="div"
    textColor="white"
    style={{
      fontWeight: 'bold',
      padding: '4px 8px',
      textAlign: 'center',
    }}
  >
    {children}
  </Element>
)

/**
 * Composant pour les cellules de données
 */
const DataCell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Element
    renderAs="div"
    style={{
      padding: '2px 8px',
      textAlign: 'center',
    }}
  >
    {children}
  </Element>
)

/**
 * Composant pour les lignes de données
 */
const DataRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Element
    renderAs="div"
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '2px 0',
    }}
  >
    {children}
  </Element>
)

/**
 * Composant pour afficher les compteurs dans un tooltip
 * Affiche les informations d'allocation des compteurs sous forme de liste structurée
 *
 * @param props - Props du composant
 * @returns Liste des compteurs avec leurs allocations
 */
export const CountersTooltip: React.FC<CountersTooltipProps> = ({ counters, templateNamesMap }) => {
  if (!counters || typeof counters !== 'object') {
    return null
  }

  try {
    return (
      <Element renderAs="div" style={{ minWidth: '200px' }}>
        {/* En-têtes des colonnes */}
        <DataRow>
          <ColumnHeader>Compteur</ColumnHeader>
          <ColumnHeader>Vers compteurs</ColumnHeader>
          <ColumnHeader>Vers semaine</ColumnHeader>
        </DataRow>

        {/* Données des compteurs */}
        {Object.keys(counters).map(counterTemplateId => {
          const counter = counters[counterTemplateId]
          const toCounter = formatDuration(counter?.allocation.toCounter || 0)
          const toPeriod = formatDuration(counter?.allocation.toPeriod || 0)
          // Prefer API name, then map from worker counters, then fallback to template id
          const displayName =
            counter?.workerCounter?.template?.name ??
            templateNamesMap?.[counterTemplateId] ??
            counterTemplateId

          return (
            <DataRow key={counterTemplateId}>
              <DataCell>{displayName}</DataCell>
              <DataCell>{toCounter}</DataCell>
              <DataCell>{toPeriod}</DataCell>
            </DataRow>
          )
        })}
      </Element>
    )
  } catch {
    return null
  }
}

export default CountersTooltip
